/**
 * @fileoverview Per-event-type QR scan configuration for useQRScanFlow.
 */
import {
  APPOINTMENT_REJECTED_MESSAGE,
  confirmBusinessEventAttendance,
  confirmB2BAttendance,
} from "../../logic/attendance/index.js";
import { updateBreakfastEventAttendance } from "../../logic/breakfastEvents/index.js";
import {
  parseB2BEventPayload,
  parseBreakfastEventPayload,
  parseBusinessEventPayload,
  parseRegistrationEventPayload,
} from "../../utils/qrScan/qrPayloadParsers.js";

const buildSuccessHandlers = (setConfirmationStatus, onQRCodeScanned) => ({
  onSuccess: (userId, eventId) => {
    setConfirmationStatus("success");
    onQRCodeScanned("success", userId, eventId);
  },
  onError: (userId, eventId, status = "error") => {
    setConfirmationStatus(status);
    onQRCodeScanned("error", userId, eventId);
  },
});

export const buildQrScanEventConfig = ({
  eventType,
  selectedEventId,
  navigate,
  confirmRegistrationAttendance,
  setConfirmationStatus,
  onQRCodeScanned,
}) => {
  if (eventType === "REGISTRATION_EVENT") {
    const handlers = buildSuccessHandlers(
      setConfirmationStatus,
      onQRCodeScanned,
    );

    return {
      parsePayload: (text) =>
        parseRegistrationEventPayload(text, selectedEventId),
      onParseFailure: () => onQRCodeScanned("invalidQRCode"),
      processScan: async ({ userId, eventId }) => {
        try {
          await confirmRegistrationAttendance(userId, eventId);
          handlers.onSuccess(userId, eventId);
        } catch {
          handlers.onError(userId, eventId);
        }
      },
    };
  }

  if (eventType === "BUSINESS_EVENT") {
    const handlers = buildSuccessHandlers(
      setConfirmationStatus,
      onQRCodeScanned,
    );

    return {
      parsePayload: (text) =>
        parseBusinessEventPayload(text, selectedEventId),
      onParseFailure: () => onQRCodeScanned("invalidQRCode"),
      processScan: async ({
        userId,
        eventId,
        buyerCompanyId,
        appointmentId,
      }) => {
        try {
          await confirmBusinessEventAttendance(
            userId,
            eventId,
            buyerCompanyId,
            appointmentId,
            true,
            navigate,
          );
          handlers.onSuccess(userId, eventId);
        } catch (error) {
          const status =
            error?.message === APPOINTMENT_REJECTED_MESSAGE
              ? "appointmentRejected"
              : "error";
          handlers.onError(userId, eventId, status);
        }
      },
    };
  }

  if (eventType === "B2B_EVENT") {
    const handlers = buildSuccessHandlers(
      setConfirmationStatus,
      onQRCodeScanned,
    );

    return {
      parsePayload: (text) => parseB2BEventPayload(text, selectedEventId),
      processScan: async ({ userId, idEventoB2B, idCita }) => {
        try {
          await confirmB2BAttendance(
            userId,
            idEventoB2B,
            idCita,
            true,
            navigate,
          );
          handlers.onSuccess(userId, idEventoB2B);
        } catch {
          handlers.onError(userId, idEventoB2B);
        }
      },
    };
  }

  if (eventType === "BREAKFAST_EVENT") {
    const handlers = buildSuccessHandlers(
      setConfirmationStatus,
      onQRCodeScanned,
    );

    return {
      parsePayload: (text) =>
        parseBreakfastEventPayload(text, selectedEventId),
      processScan: async ({ userId, idEventoDesayuno }) => {
        try {
          await updateBreakfastEventAttendance(
            userId,
            idEventoDesayuno,
            true,
            navigate,
          );
          handlers.onSuccess(userId, idEventoDesayuno);
        } catch {
          handlers.onError(userId, idEventoDesayuno);
        }
      },
    };
  }

  return null;
};
